import { useState, useCallback, useEffect } from 'react';
import { ArrowGoIcon, NumpadSvgIcon, DeleteIcon } from '@/icons';
import styles from '@/components/jog/numpad.module.css';

interface NumpadKeyboardProps {
  label: string;
  onSubmit: (value: string) => void;
  onClose: () => void;
  /** Fires on every keystroke so the caller can mirror the live value. */
  onChange?: (value: string) => void;
  /** Text on the confirm key (defaults to "Go"; e.g. "Set" in the tool form). */
  submitLabel?: string;
}

type KeyId =
  | '0'
  | '1'
  | '2'
  | '3'
  | '4'
  | '5'
  | '6'
  | '7'
  | '8'
  | '9'
  | '-'
  | ','
  | 'delete'
  | 'go'
  | 'keyboard';

/** Numpad bottom sheet for entering numeric values. */
export function NumpadKeyboard({
  label,
  onSubmit,
  onClose,
  onChange,
  submitLabel = 'Go',
}: NumpadKeyboardProps): JSX.Element {
  // Current displayed value
  const [input, setInput] = useState('');
  // Which key is visually highlighted (cleared after 120ms)
  const [activeKey, setActiveKey] = useState<KeyId | null>(null);

  // Mirror the live value out so the caller can reflect it before Go commits.
  useEffect(() => {
    onChange?.(input);
  }, [input, onChange]);

  /** Flash the active key indicator for 120ms. */
  const flashKey = useCallback((key: KeyId) => {
    setActiveKey(key);
    setTimeout(() => setActiveKey(null), 120);
  }, []);

  const handleDigit = useCallback(
    (digit: string, key: KeyId) => {
      flashKey(key);
      setInput((prev) => prev + digit);
    },
    [flashKey],
  );

  const handleDelete = useCallback(() => {
    flashKey('delete');
    setInput((prev) => prev.slice(0, -1));
  }, [flashKey]);

  const handleGo = useCallback(() => {
    flashKey('go');
    onSubmit(input);
    onClose();
  }, [input, onSubmit, onClose, flashKey]);

  const handleKeyboard = useCallback(() => {
    flashKey('keyboard');
    onClose();
  }, [onClose, flashKey]);

  // Physical keyboard support
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key >= '0' && e.key <= '9') handleDigit(e.key, e.key as KeyId);
      else if (e.key === '-') handleDigit('-', '-');
      else if (e.key === ',' || e.key === '.') handleDigit(',', ',');
      else if (e.key === 'Backspace') handleDelete();
      else if (e.key === 'Enter') handleGo();
      else if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [handleDigit, handleDelete, handleGo, onClose]);

  const keyClass = (key: KeyId, extra?: string) =>
    [styles.numpadKey, extra ?? '', activeKey === key ? styles.numpadKeyActive : '']
      .filter(Boolean)
      .join(' ');

  return (
    <div
      className={styles.numpadOverlay}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={`${label} input`}
    >
      <div className={styles.numpadSheet} onClick={(e) => e.stopPropagation()}>
        {/* Key grid: 4 rows × 4 cols (3 digit cols + 1 action col) */}
        <div className={styles.numpadGrid}>
          {/* Row 1 */}
          <button className={keyClass('7')} type="button" onClick={() => handleDigit('7', '7')}>
            7
          </button>
          <button className={keyClass('8')} type="button" onClick={() => handleDigit('8', '8')}>
            8
          </button>
          <button className={keyClass('9')} type="button" onClick={() => handleDigit('9', '9')}>
            9
          </button>
          <button
            className={keyClass('delete', styles.numpadKeyDelete)}
            type="button"
            aria-label="Delete last character"
            onClick={handleDelete}
          >
            <DeleteIcon width={28} height={22} />
          </button>

          {/* Row 2 */}
          <button className={keyClass('4')} type="button" onClick={() => handleDigit('4', '4')}>
            4
          </button>
          <button className={keyClass('5')} type="button" onClick={() => handleDigit('5', '5')}>
            5
          </button>
          <button className={keyClass('6')} type="button" onClick={() => handleDigit('6', '6')}>
            6
          </button>
          {/* Go spans rows 2–3 */}
          <button
            className={keyClass('go', styles.numpadKeyGo)}
            type="button"
            aria-label="Confirm value"
            onClick={handleGo}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              {submitLabel} <ArrowGoIcon width={24} height={22} />
            </span>
          </button>

          {/* Row 3 */}
          <button className={keyClass('1')} type="button" onClick={() => handleDigit('1', '1')}>
            1
          </button>
          <button className={keyClass('2')} type="button" onClick={() => handleDigit('2', '2')}>
            2
          </button>
          <button className={keyClass('3')} type="button" onClick={() => handleDigit('3', '3')}>
            3
          </button>
          {/* 4th cell in row 3 is occupied by Go (span 2) — no element needed */}

          {/* Row 4 */}
          <button className={keyClass('-')} type="button" onClick={() => handleDigit('-', '-')}>
            −
          </button>
          <button className={keyClass('0')} type="button" onClick={() => handleDigit('0', '0')}>
            0
          </button>
          <button className={keyClass(',')} type="button" onClick={() => handleDigit(',', ',')}>
            ,
          </button>
          <button
            className={keyClass('keyboard', styles.numpadKeyKeyboard)}
            type="button"
            aria-label="Close numpad"
            onClick={handleKeyboard}
          >
            <NumpadSvgIcon width={32} height={26} />
          </button>
        </div>

        {/* iPhone-style home indicator */}
        <div className={styles.numpadHomeBar} />
      </div>
    </div>
  );
}
